import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import DetailSection from '@/components/companies/detail/detail-section'
import CompanyMark from '@/components/generals/company-mark'
import InitialsAvatar from '@/components/generals/initials-avatar'
import ActivityIcon from '@/components/activities/activity-icon'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ownerByName } from '@/constants/companies'
import { formatLongDate, formatShortDate } from '@/lib/dates'
import { useCrmStore } from '@/stores/crm-store-provider'
import BookClosedIcon from '@/assets/images/companies/sidebar/book-closed.svg'
import XIcon from '@/assets/images/companies/detail/x.svg'
import MailIcon from '@/assets/images/companies/detail/mail-04.svg'
import PhoneIcon from '@/assets/images/companies/detail/phone.svg'

import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

export default function ContactDetail() {
    const contactId = useCrmStore(state => state.contactId)
    const contacts = useCrmStore(state => state.contacts)
    const companies = useCrmStore(state => state.companies)
    const activities = useCrmStore(state => state.activities)
    const closeContact = useCrmStore(state => state.closeContact)
    const openDetail = useCrmStore(state => state.openDetail)
    const openProfile = useCrmStore(state => state.openProfile)
    const openDialog = useCrmStore(state => state.openDialog)

    const contact = contacts.find(item => item.id === contactId)
    const company = contact?.companyId ? companies.find(item => item.id === contact.companyId) : undefined
    const owner = contact ? ownerByName(contact.owner) : null
    const history = contact
        ? activities
            .filter(activity => activity.contactId === contact.id)
            .sort((a, b) => b.dueDate.localeCompare(a.dueDate))
        : []

    return (
        <Sheet open={contact !== undefined} onOpenChange={open => !open && closeContact()}>
            <SheetContent side='right' className='sm:w-[480px] sm:max-w-[480px]'>
                <SheetHeader>
                    <div className='flex items-center gap-2'>
                        <BookClosedIcon aria-hidden className='text-icon size-3.5' />
                        <SheetTitle>Contact</SheetTitle>
                    </div>
                    <SheetDescription className='sr-only'>
                        Contact details, account and activity history
                    </SheetDescription>
                    <SheetClose asChild>
                        <Button variant='ghost' size='icon-sm' className='-mr-1' aria-label="Close contact">
                            <XIcon aria-hidden className='text-foreground size-4' />
                        </Button>
                    </SheetClose>
                </SheetHeader>

                {contact && owner && (
                    <ScrollArea className='min-h-0 flex-1'>
                        <div className='flex items-center gap-3 p-5 shadow-[inset_0_-1px_0_var(--line-strong)]'>
                            <InitialsAvatar name={contact.name} className='size-[50px] text-[16px]' />
                            <div className='flex min-w-0 flex-col gap-2'>
                                <h2 className='truncate' data-mdlfy-cms={`contacts:${contact.id}:name`}>
                                    {contact.name}
                                </h2>
                                <span className='caption-style text-soft flex min-w-0 items-center gap-1'>
                                    <span className='truncate' data-mdlfy-cms={`contacts:${contact.id}:title`}>
                                        {contact.title}
                                    </span>
                                    {company && (
                                        <>
                                            <span className='text-subtle shrink-0'>at</span>
                                            <span className='truncate' data-mdlfy-cms={`companies:${company.id}:name`}>
                                                {company.name}
                                            </span>
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>

                        <DetailSection title="Contact details">
                            <div className='lead-style flex flex-col gap-3'>
                                {contact.email ? (
                                    <a href={`mailto:${contact.email}`} className='hover:text-soft flex min-w-0 items-center gap-2 transition-colors duration-150'>
                                        <MailIcon aria-hidden className='text-soft size-3 shrink-0' />
                                        <span className='truncate' data-mdlfy-cms={`contacts:${contact.id}:email`}>
                                            {contact.email}
                                        </span>
                                    </a>
                                ) : (
                                    <span className='caption-style text-subtle'>No email saved</span>
                                )}
                                {contact.phone ? (
                                    <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`} className='hover:text-soft flex items-center gap-2 transition-colors duration-150'>
                                        <PhoneIcon aria-hidden className='text-soft size-3 shrink-0' />
                                        <span data-mdlfy-cms={`contacts:${contact.id}:phone`}>
                                            {contact.phone}
                                        </span>
                                    </a>
                                ) : (
                                    <span className='caption-style text-subtle'>No phone saved</span>
                                )}
                                <span className='caption-style text-soft'>
                                    Last contacted{' '}
                                    <span className='text-foreground'>
                                        {contact.lastContacted ? formatLongDate(contact.lastContacted) : "not yet"}
                                    </span>
                                </span>
                            </div>
                        </DetailSection>

                        <DetailSection title="Account">
                            <div className='flex flex-wrap items-center gap-x-4 gap-y-3'>
                                {company ? (
                                    <Button
                                        variant='ghost'
                                        size='none'
                                        onClick={() => openDetail(company.id)}
                                        className='lead-style text-foreground -mx-1.5 gap-2 px-1.5 py-1 font-medium'
                                    >
                                        <CompanyMark company={company} className='size-5 rounded-[5px]' />
                                        <span data-mdlfy-cms={`companies:${company.id}:name`}>
                                            {company.name}
                                        </span>
                                    </Button>
                                ) : (
                                    <span className='caption-style text-subtle'>No company linked</span>
                                )}
                                <Button
                                    variant='ghost'
                                    size='none'
                                    onClick={() => openProfile(owner.name)}
                                    aria-label={`Open ${owner.name} profile`}
                                    className='lead-style text-foreground -mx-1.5 gap-1.5 px-1.5 py-1 font-normal'
                                >
                                    <Avatar src={owner.avatar} alt='' />
                                    <span data-mdlfy-cms={`contacts:${contact.id}:owner`}>
                                        {contact.owner}
                                    </span>
                                </Button>
                            </div>
                        </DetailSection>

                        <DetailSection title="Activity history" className='shadow-none'>
                            {history.length > 0 ? (
                                <ul className='flex flex-col gap-3'>
                                    {history.map(activity => (
                                        <li key={activity.id} className='flex items-start gap-3'>
                                            <ActivityIcon type={activity.type} />
                                            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                                                <span className='lead-style block' data-mdlfy-cms={`activities:${activity.id}:subject`}>
                                                    {activity.subject}
                                                </span>
                                                <span className='caption-style text-subtle'>
                                                    {formatShortDate(activity.dueDate)}
                                                    {activity.completed ? " · Done" : " · Open"}
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <span className='caption-style text-subtle block'>
                                    No activities logged with this contact yet.
                                </span>
                            )}
                        </DetailSection>
                    </ScrollArea>
                )}

                <SheetFooter>
                    <SheetClose asChild>
                        <Button variant='subtle' size='sm'>
                            Close
                        </Button>
                    </SheetClose>
                    <Button
                        variant='primary'
                        size='sm'
                        onClick={() => contact && openDialog('activity', { contactId: contact.id, companyId: contact.companyId ?? undefined })}
                    >
                        Log activity
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    )
}
